/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: ChartsSection 图表信息展示模块
 */
import React, { useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import GenreShareCard from './GenreShareCard';
import GenreTrendCard from './GenreTrendCard';
import SubGenrePerformanceCard from './SubGenrePerformanceCard';
import SubGenreTrendCard from './SubGenreTrendCard';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import {
  fetchCasualGenreProfileMainGenreMetrics,
  fetchCasualGenreProfileMainGenreMetricsSeparateByDate,
  fetchCasualGenreProfileSubGenreMetrics,
  fetchCasualGenreProfileSubGenreMetricsSeparateByDate,
  fetchCasualGenreProfileSuperGenreMetrics,
  fetchCasualGenreProfileSuperGenreMetricsSeparateByDate,
  fetchCasualGenreProfileMajorCategoryMetrics,
  fetchCasualGenreProfileMajorCategoryMetricsSeparateByDate,
  fetchCasualGenreProfileMidCategoryMetrics,
  fetchCasualGenreProfileMidCategoryMetricsSeparateByDate,
} from '../../services';
import { getGenreNameI18n } from '@intelligence/v3/utils/genre';
import { xAxisLabelFormatterOfTrendLineChartWithGranularity } from './utils';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import { GENRE_DIMENSION_MAP } from '../../constants';

import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';
import type {
  TDataSourceType,
  TMetricType,
  TDimension,
  IGenreModuleInstance,
  IGenreModuleUpdateParams,
} from '../../types';
import type {
  IGenrePieChartClickParams,
  IGenreBarChartClickParams,
  IGenreHorizontalBarChartClickParams,
} from './types';

import './index.scss';

/**
 * ChartsSection 组件 Props
 */
export interface IChartsSectionProps extends IBaseProps {
  /** 品类选项数据 map */
  genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  /** 品类数据源 */
  genreSource: IGenreSource;
  /** 当前选中的日期粒度 */
  granularity: IGranularity;
  /** 当前选中的数据源 */
  dataSource: TDataSourceType;
  /** 当前选中的指标 */
  metric: TMetricType;
  /** 筛选参数（包含粒度信息） */
  filterParams?: IRecord;
  /** 饼状图扇形点击事件回调（接收带品类维度的参数） */
  onPieClick?: (params: IGenrePieChartClickParams) => void;
  /** 柱状图柱子点击事件回调（接收带品类维度的参数） */
  onBarClick?: (params: IGenreBarChartClickParams) => void;
  /** 横向柱状图柱子点击事件回调（接收带品类维度的参数） */
  onHorizontalBarClick?: (params: IGenreHorizontalBarChartClickParams) => void;
}

/**
 * ChartsSection 图表信息展示模块
 * @description 包含主品类和副品类的图表展示
 * - SensorTower: 2行图表（Main Genre + Sub Genre）
 * - AppMagic: 3行图表（Super Genre + Level-1/2 Genre）
 */
const ChartsSection: React.ForwardRefRenderFunction<IGenreModuleInstance, IChartsSectionProps> = (props, ref) => {
  const { style, className, genreOptionsMap, genreSource, granularity, dataSource, metric, filterParams, onPieClick, onBarClick, onHorizontalBarClick } =
    props;

  const { locale, formatMessage } = useIntlExtend();

  const [mainGenresLoading, setMainGenresLoading] = useState(true);
  const [mainGenres, setMainGenres] = useState<ICasualGenreProfileMetricData[]>([]);
  const [separatedMainGenresLoading, setSeparatedMainGenresLoading] = useState(true);
  const [separatedMainGenres, setSeparatedMainGenres] = useState<ICasualGenreProfileMetricData[]>([]);
  const [subGenresLoading, setSubGenresLoading] = useState(true);
  const [subGenres, setSubGenres] = useState<ICasualGenreProfileMetricData[]>([]);
  const [separatedSubGenresLoading, setSeparatedSubGenresLoading] = useState(true);
  const [separatedSubGenres, setSeparatedSubGenres] = useState<ICasualGenreProfileMetricData[]>([]);

  const [superGenresLoading, setSuperGenresLoading] = useState(true);
  const [superGenres, setSuperGenres] = useState<ICasualGenreProfileMetricData[]>([]);
  const [separatedSuperGenresLoading, setSeparatedSuperGenresLoading] = useState(true);
  const [separatedSuperGenres, setSeparatedSuperGenres] = useState<ICasualGenreProfileMetricData[]>([]);
  const [majorCategoriesLoading, setMajorCategoriesLoading] = useState(true);
  const [majorCategories, setMajorCategories] = useState<ICasualGenreProfileMetricData[]>([]);
  const [separatedMajorCategoriesLoading, setSeparatedMajorCategoriesLoading] = useState(true);
  const [separatedMajorCategories, setSeparatedMajorCategories] = useState<ICasualGenreProfileMetricData[]>([]);
  const [midCategoriesLoading, setMidCategoriesLoading] = useState(true);
  const [midCategories, setMidCategories] = useState<ICasualGenreProfileMetricData[]>([]);
  const [separatedMidCategoriesLoading, setSeparatedMidCategoriesLoading] = useState(true);
  const [separatedMidCategories, setSeparatedMidCategories] = useState<ICasualGenreProfileMetricData[]>([]);

  const xAxisLabelFormatterOfTrendLineChart = (params: string) => {
    return xAxisLabelFormatterOfTrendLineChartWithGranularity(granularity, params);
  };

  const formatGenreName = (genreValue: string) => {
    if (genreValue === 'others') {
      return formatMessage({ id: 'dbi_others' });
    }

    return getGenreNameI18n({ locale, genreOptionsMap: genreOptionsMap || {}, genreSource, genreValue });
  };

  const labelFormatOfPieChart = (params: any) => {
    const paramsName = params?.name || '';
    const paramsPercent = params?.percent ?? null;
    const formatedName = formatGenreName?.(paramsName) || paramsName || '';

    if (paramsPercent === null) {
      return formatedName;
    }

    return `${formatedName}: \n${paramsPercent}%`;
  };

  /**
   * 创建带品类维度的饼状图点击处理函数
   * @param genreDimension 品类维度
   * @description 接收 PieChart 组件的 IPieChartClickParams，添加品类维度后传递给父组件
   */
  const createPieClickHandler = (genreDimension: TDimension) => {
    return (params: IPieChartClickParams) => {
      const paramsNew: IGenrePieChartClickParams = {
        ...params,
        genreDimension,
      } as IGenrePieChartClickParams;
      onPieClick?.(paramsNew);
    };
  };

  // 各品类维度的饼状图点击处理函数
  const handleMainGenrePieClick = createPieClickHandler(GENRE_DIMENSION_MAP.MAIN_GENRE);
  const handleSubGenrePieClick = createPieClickHandler(GENRE_DIMENSION_MAP.SUB_GENRE);
  const handleSuperGenrePieClick = createPieClickHandler(GENRE_DIMENSION_MAP.SUPER_GENRE);
  const handleMajorCategoryPieClick = createPieClickHandler(GENRE_DIMENSION_MAP.MAJOR_CATEGORY);
  const handleMidCategoryPieClick = createPieClickHandler(GENRE_DIMENSION_MAP.MID_CATEGORY);

  /**
   * 创建带品类维度的柱状图点击处理函数
   * @param genreDimension 品类维度
   */
  const createBarClickHandler = (genreDimension: TDimension) => {
    return (params: any) => {
      const paramsNew: IGenreBarChartClickParams = {
        ...params,
        genreDimension,
      };
      onBarClick?.(paramsNew);
    };
  };

  /**
   * 创建带品类维度的横向柱状图点击处理函数
   * @param genreDimension 品类维度
   */
  const createHorizontalBarClickHandler = (genreDimension: TDimension) => {
    return (params: any) => {
      const paramsNew: IGenreHorizontalBarChartClickParams = {
        ...params,
        genreDimension,
      };
      onHorizontalBarClick?.(paramsNew);
    };
  };

  // 各品类维度的柱状图点击处理函数
  const handleMainGenreBarClick = createBarClickHandler(GENRE_DIMENSION_MAP.MAIN_GENRE);
  const handleSubGenreBarClick = createBarClickHandler(GENRE_DIMENSION_MAP.SUB_GENRE);
  const handleSuperGenreBarClick = createBarClickHandler(GENRE_DIMENSION_MAP.SUPER_GENRE);
  const handleMajorCategoryBarClick = createBarClickHandler(GENRE_DIMENSION_MAP.MAJOR_CATEGORY);
  const handleMidCategoryBarClick = createBarClickHandler(GENRE_DIMENSION_MAP.MID_CATEGORY);

  const updateBarAndPieChartsDataOfSensortowerMainGenres = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setMainGenresLoading(true);
    try {
      const res = await fetchCasualGenreProfileMainGenreMetrics(dataSource, metric, filterParams);
      const resData = res?.data;
      const mainGenresNew = resData?.list || [];
      setMainGenres(mainGenresNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileMainGenreMetrics error:', error);
      setMainGenres([]);
    } finally {
      setMainGenresLoading(false);
    }
  };

  const updateBarAndPieChartsDataOfSensortowerSubGenres = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setSubGenresLoading(true);
    try {
      const res = await fetchCasualGenreProfileSubGenreMetrics(dataSource, metric, filterParams);
      const resData = res?.data;
      const subGenresNew = resData?.list || [];
      setSubGenres(subGenresNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileSubGenreMetrics error:', error);
      setSubGenres([]);
    } finally {
      setSubGenresLoading(false);
    }
  };

  const updateBarAndPieChartsDataOfSensortower = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    void updateBarAndPieChartsDataOfSensortowerMainGenres(dataSource, metric, filterParams);
    void updateBarAndPieChartsDataOfSensortowerSubGenres(dataSource, metric, filterParams);
  };

  const updateLineChartDataOfSensortowerMainGenres = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setSeparatedMainGenresLoading(true);
    try {
      const res = await fetchCasualGenreProfileMainGenreMetricsSeparateByDate(dataSource, metric, filterParams);
      const resData = res?.data;
      const separatedMainGenresNew = resData?.list || [];
      setSeparatedMainGenres(separatedMainGenresNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileMainGenreMetricsSeparateByDate error:', error);
      setSeparatedMainGenres([]);
    } finally {
      setSeparatedMainGenresLoading(false);
    }
  };

  const updateLineChartDataOfSensortowerSubGenres = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setSeparatedSubGenresLoading(true);
    try {
      const res = await fetchCasualGenreProfileSubGenreMetricsSeparateByDate(dataSource, metric, filterParams);
      const resData = res?.data;
      const separatedSubGenresNew = resData?.list || [];
      setSeparatedSubGenres(separatedSubGenresNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileSubGenreMetricsSeparateByDate error:', error);
      setSeparatedSubGenres([]);
    } finally {
      setSeparatedSubGenresLoading(false);
    }
  };

  const updateLineChartDataOfSensortower = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    void updateLineChartDataOfSensortowerMainGenres(dataSource, metric, filterParams);
    void updateLineChartDataOfSensortowerSubGenres(dataSource, metric, filterParams);
  };

  const updateBarAndPieChartsDataOfAppMagicSuperGenres = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setSuperGenresLoading(true);
    try {
      const res = await fetchCasualGenreProfileSuperGenreMetrics(dataSource, metric, filterParams);
      const resData = res?.data;
      const superGenresNew = resData?.list || [];
      setSuperGenres(superGenresNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileSuperGenreMetrics error:', error);
      setSuperGenres([]);
    } finally {
      setSuperGenresLoading(false);
    }
  };

  const updateBarAndPieChartsDataOfAppMagicMajorCategories = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setMajorCategoriesLoading(true);
    try {
      const res = await fetchCasualGenreProfileMajorCategoryMetrics(dataSource, metric, filterParams);
      const resData = res?.data;
      const majorCategoriesNew = resData?.list || [];
      setMajorCategories(majorCategoriesNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileMajorCategoryMetrics error:', error);
      setMajorCategories([]);
    } finally {
      setMajorCategoriesLoading(false);
    }
  };

  const updateBarAndPieChartsDataOfAppMagicMidCategories = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setMidCategoriesLoading(true);
    try {
      const res = await fetchCasualGenreProfileMidCategoryMetrics(dataSource, metric, filterParams);
      const resData = res?.data;
      const midCategoriesNew = resData?.list || [];
      setMidCategories(midCategoriesNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileMidCategoryMetrics error:', error);
      setMidCategories([]);
    } finally {
      setMidCategoriesLoading(false);
    }
  };

  const updateBarAndPieChartsDataOfAppMagic = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    void updateBarAndPieChartsDataOfAppMagicSuperGenres(dataSource, metric, filterParams);
    void updateBarAndPieChartsDataOfAppMagicMajorCategories(dataSource, metric, filterParams);
    void updateBarAndPieChartsDataOfAppMagicMidCategories(dataSource, metric, filterParams);
  };

  const updateLineChartDataOfAppMagicSuperGenres = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setSeparatedSuperGenresLoading(true);
    try {
      const res = await fetchCasualGenreProfileSuperGenreMetricsSeparateByDate(dataSource, metric, filterParams);
      const resData = res?.data;
      const separatedSuperGenresNew = resData?.list || [];
      setSeparatedSuperGenres(separatedSuperGenresNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileSuperGenreMetricsSeparateByDate error:', error);
      setSeparatedSuperGenres([]);
    } finally {
      setSeparatedSuperGenresLoading(false);
    }
  };

  const updateLineChartDataOfAppMagicMajorCategories = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setSeparatedMajorCategoriesLoading(true);
    try {
      const res = await fetchCasualGenreProfileMajorCategoryMetricsSeparateByDate(dataSource, metric, filterParams);
      const resData = res?.data;
      const separatedMajorCategoriesNew = resData?.list || [];
      setSeparatedMajorCategories(separatedMajorCategoriesNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileMajorCategoryMetricsSeparateByDate error:', error);
      setSeparatedMajorCategories([]);
    } finally {
      setSeparatedMajorCategoriesLoading(false);
    }
  };

  const updateLineChartDataOfAppMagicMidCategories = async (
    dataSource: TDataSourceType,
    metric: TMetricType,
    filterParams: any,
  ) => {
    setSeparatedMidCategoriesLoading(true);
    try {
      const res = await fetchCasualGenreProfileMidCategoryMetricsSeparateByDate(dataSource, metric, filterParams);
      const resData = res?.data;
      const separatedMidCategoriesNew = resData?.list || [];
      setSeparatedMidCategories(separatedMidCategoriesNew);
    } catch (error) {
      console.error('fetchCasualGenreProfileMidCategoryMetricsSeparateByDate error:', error);
      setSeparatedMidCategories([]);
    } finally {
      setSeparatedMidCategoriesLoading(false);
    }
  };

  const updateLineChartDataOfAppMagic = async (dataSource: TDataSourceType, metric: TMetricType, filterParams: any) => {
    void updateLineChartDataOfAppMagicSuperGenres(dataSource, metric, filterParams);
    void updateLineChartDataOfAppMagicMajorCategories(dataSource, metric, filterParams);
    void updateLineChartDataOfAppMagicMidCategories(dataSource, metric, filterParams);
  };

  useImperativeHandle(ref, () => ({
    update: (params: IGenreModuleUpdateParams) => {
      const { genreSource, dataSource, metric, filterParams } = params || {};
      switch (genreSource) {
        case GENRE_SOURCE_MAP.SENSORTOWER: {
          void updateBarAndPieChartsDataOfSensortower(dataSource, metric, filterParams);
          void updateLineChartDataOfSensortower(dataSource, metric, filterParams);
          break;
        }
        case GENRE_SOURCE_MAP.APP_MAGIC: {
          void updateBarAndPieChartsDataOfAppMagic(dataSource, metric, filterParams);
          void updateLineChartDataOfAppMagic(dataSource, metric, filterParams);
          break;
        }
        default: {
        }
      }
    },
  }));

  return (
    <div style={style} className={classnames('dbi-charts-section', className)}>
      {/* 渲染 SensorTower 数据源的图表（2行） */}
      {genreSource === GENRE_SOURCE_MAP.SENSORTOWER && (
        <>
          {/* 第一行：Main Genre 图表 */}
          <div className="dbi-charts-section__row">
            {/* 左侧：Genre Share - Main Genre 饼状图 */}
            <div className="dbi-charts-section__card">
              <GenreShareCard
                loading={mainGenresLoading}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.MAIN_GENRE}
                dataSource={dataSource}
                metric={metric}
                list={mainGenres}
                formatGenreName={formatGenreName}
                labelFormatOfPieChart={labelFormatOfPieChart}
                onPieClick={handleMainGenrePieClick}
              />
            </div>

            {/* 右侧：Genre Trend - Main Genre 趋势图 */}
            <div className="dbi-charts-section__card">
              <GenreTrendCard
                loading={mainGenresLoading}
                separatedLoading={separatedMainGenresLoading}
                granularity={filterParams?.granularity}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.MAIN_GENRE}
                dataSource={dataSource}
                metric={metric}
                list={mainGenres}
                separatedList={separatedMainGenres}
                formatGenreName={formatGenreName}
                labelFormatOfPieChart={labelFormatOfPieChart}
                xAxisLabelFormatterOfTrendLineChart={xAxisLabelFormatterOfTrendLineChart}
                onPieClick={handleMainGenrePieClick}
                onBarClick={handleMainGenreBarClick}
              />
            </div>
          </div>

          {/* 第二行：Sub Genre 图表 */}
          <div className="dbi-charts-section__row">
            {/* 左侧：Genre Performance - Sub Genre 横向柱状图 */}
            <div className="dbi-charts-section__card">
              <SubGenrePerformanceCard
                loading={subGenresLoading}
                title={formatMessage({ id: 'dbi_casual_genre_share_sub_genre' })}
                genreOptionsMap={genreOptionsMap}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.SUB_GENRE}
                dataSource={dataSource}
                metric={metric}
                list={subGenres}
                formatGenreName={formatGenreName}
                labelFormatOfPieChart={labelFormatOfPieChart}
                onHorizontalBarClick={handleSubGenreBarClick}
              />
            </div>

            {/* 右侧：Genre Trend - Sub Genre 趋势图 */}
            <div className="dbi-charts-section__card">
              <SubGenreTrendCard
                loading={subGenresLoading}
                separatedLoading={separatedSubGenresLoading}
                title={formatMessage({ id: 'dbi_casual_genre_trend_sub_genre' })}
                genreOptionsMap={genreOptionsMap}
                granularity={filterParams?.granularity}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.SUB_GENRE}
                dataSource={dataSource}
                metric={metric}
                list={subGenres}
                separatedList={separatedSubGenres}
                formatGenreName={formatGenreName}
                labelFormatOfPieChart={labelFormatOfPieChart}
                xAxisLabelFormatterOfTrendLineChart={xAxisLabelFormatterOfTrendLineChart}
                onPieClick={handleSubGenrePieClick}
                onBarClick={handleSubGenreBarClick}
              />
            </div>
          </div>
        </>
      )}
      {/* 渲染 AppMagic 数据源的图表（3行）*/}
      {genreSource === GENRE_SOURCE_MAP.APP_MAGIC && (
        <>
          {/* 第一行：Super Genre 图表 */}
          <div className="dbi-charts-section__row">
            {/* 左侧：Genre Share - Super Genre 饼状图 */}
            <div className="dbi-charts-section__card">
              <GenreShareCard
                loading={superGenresLoading}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.SUPER_GENRE}
                dataSource={dataSource}
                metric={metric}
                list={superGenres}
                formatGenreName={formatGenreName}
                labelFormatOfPieChart={labelFormatOfPieChart}
                onPieClick={handleSuperGenrePieClick}
              />
            </div>

            {/* 右侧：Genre Trend - Super Genre 趋势图 */}
            <div className="dbi-charts-section__card">
              <GenreTrendCard
                loading={superGenresLoading}
                separatedLoading={separatedSuperGenresLoading}
                genreOptionsMap={genreOptionsMap}
                granularity={filterParams?.granularity}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.SUPER_GENRE}
                dataSource={dataSource}
                metric={metric}
                list={superGenres}
                separatedList={separatedSuperGenres}
                formatGenreName={formatGenreName}
                labelFormatOfPieChart={labelFormatOfPieChart}
                xAxisLabelFormatterOfTrendLineChart={xAxisLabelFormatterOfTrendLineChart}
                onPieClick={handleSuperGenrePieClick}
                onBarClick={handleSuperGenreBarClick}
              />
            </div>
          </div>

          {/* 第二行：Level-1 Genre 图表 */}
          <div className="dbi-charts-section__row">
            {/* 左侧：Genre Performance - Level-1 Genre 横向柱状图 */}
            <div className="dbi-charts-section__card">
              <SubGenrePerformanceCard
                loading={majorCategoriesLoading}
                title={formatMessage({ id: 'dbi_casual_genre_performance_level1_genre' })}
                genreOptionsMap={genreOptionsMap}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.MAJOR_CATEGORY}
                dataSource={dataSource}
                metric={metric}
                list={majorCategories}
                formatGenreName={formatGenreName}
                onHorizontalBarClick={handleMajorCategoryBarClick}
              />
            </div>

            {/* 右侧：Genre Trend - Level-1 Genre 趋势图 */}
            <div className="dbi-charts-section__card">
              <SubGenreTrendCard
                loading={majorCategoriesLoading}
                separatedLoading={separatedMajorCategoriesLoading}
                title={formatMessage({ id: 'dbi_casual_genre_trend_level1_genre' })}
                genreOptionsMap={genreOptionsMap}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.MAJOR_CATEGORY}
                dataSource={dataSource}
                metric={metric}
                list={majorCategories}
                separatedList={separatedMajorCategories}
                formatGenreName={formatGenreName}
                labelFormatOfPieChart={labelFormatOfPieChart}
                granularity={filterParams?.granularity}
                xAxisLabelFormatterOfTrendLineChart={xAxisLabelFormatterOfTrendLineChart}
                onPieClick={handleMajorCategoryPieClick}
                onBarClick={handleMajorCategoryBarClick}
              />
            </div>
          </div>

          {/* 第三行：Level-2 Genre 图表 */}
          <div className="dbi-charts-section__row">
            {/* 左侧：Genre Performance - Level-2 Genre 横向柱状图 */}
            <div className="dbi-charts-section__card">
              <SubGenrePerformanceCard
                loading={midCategoriesLoading}
                title={formatMessage({ id: 'dbi_casual_genre_performance_level2_genre' })}
                genreOptionsMap={genreOptionsMap}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.MID_CATEGORY}
                dataSource={dataSource}
                metric={metric}
                list={midCategories}
                formatGenreName={formatGenreName}
                onHorizontalBarClick={handleMidCategoryBarClick}
              />
            </div>

            {/* 右侧：Genre Trend - Level-2 Genre 趋势图 */}
            <div className="dbi-charts-section__card">
              <SubGenreTrendCard
                loading={midCategoriesLoading}
                separatedLoading={separatedMidCategoriesLoading}
                title={formatMessage({ id: 'dbi_casual_genre_trend_level2_genre' })}
                genreOptionsMap={genreOptionsMap}
                genreSource={genreSource}
                genreDimension={GENRE_DIMENSION_MAP.MID_CATEGORY}
                dataSource={dataSource}
                metric={metric}
                list={midCategories}
                separatedList={separatedMidCategories}
                formatGenreName={formatGenreName}
                labelFormatOfPieChart={labelFormatOfPieChart}
                granularity={filterParams?.granularity}
                xAxisLabelFormatterOfTrendLineChart={xAxisLabelFormatterOfTrendLineChart}
                onPieClick={handleMidCategoryPieClick}
                onBarClick={handleMidCategoryBarClick}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// @ts-ignore
export default React.forwardRef(ChartsSection);
